import { Auth } from "aws-amplify";
import { useEffect, useRef } from "react";

export function useTraceUpdate(props: Record<string, any>) {
  const prev = useRef(props);
  useEffect(() => {
    const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
      if (prev.current[k] !== v) {
        (ps as any)[k] = [prev.current[k], v];
      }
      return ps;
    }, {});
    if (Object.keys(changedProps).length > 0) {
      console.log("Changed props:", changedProps);
    }
    prev.current = props;
  });
}

export function overrideWindowFetch() {
  const originalFetch = window.fetch;
  window.fetch = async (url, options) => {
    let _url;
    if (typeof url === "string") _url = url;
    else _url = (url as Request).url;

    if (!_url.includes(process.env.REACT_APP_API_ENDPOINT || ""))
      return originalFetch(url, options);

    if (!options) options = {};
    const session = await Auth.currentSession().catch((e) => null);

    if (session) {
      const headers = options?.headers || {};
      options.headers = {
        Authorization: session.getIdToken().getJwtToken(),
        access_token: session.getAccessToken().getJwtToken(),
        id_token: session.getIdToken().getJwtToken(),
        ...headers,
      };
    }

    return originalFetch(url, options);
  };
}

export function toCsv(rows: Record<string, any>[], headers: string[]): string {
  const escape = (value: any) => {
    if (value === null || value === undefined) return "";
    const str = String(value).replace(/"/g, '""');
    return /[",\n]/.test(str) ? `"${str}"` : str;
  };

  const lines = [
    headers.join(","),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(",")),
  ];

  return lines.join("\n");
}

export function downloadCsvFile(csv: string, fileName: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(url);
}

export async function fetchAllPages<T>(
  api: (
    variables: any,
    signal?: AbortSignal,
  ) => Promise<{ Items: T[]; NextToken?: string; MaxPages?: number }>,
  baseVariables: any,
  pageSize = 1000,
  maxLoops = 200,
): Promise<T[]> {
  const results: T[] = [];
  let nextToken: string | undefined;

  for (let i = 0; i < maxLoops; i++) {
    const variables = {
      ...baseVariables,
      queryParams: {
        ...(baseVariables?.queryParams || {}),
        PageSize: pageSize,
        ...(nextToken ? { NextToken: nextToken } : {}),
      },
    };

    const response = await api(variables);
    if (response?.Items?.length) results.push(...response.Items);

    if (!response?.NextToken) break;
    nextToken = response.NextToken;
  }

  return results;
}
